import React, { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import { MdEdit, MdDragIndicator, MdClose } from 'react-icons/md';
import { FaTriangleExclamation } from "react-icons/fa6";
import {HiArrowNarrowDown,HiArrowNarrowUp} from 'react-icons/hi';
import styles from './customDataTable.module.scss';
import { AddButton } from './buttons';
import { PopupOnClick } from './itemOnPopup';
import { generateShortId, dateFormatter } from '../../../supplementary_components/supplementaryMethods';
import { addPopup } from '../../../supplementary_components/store';
import { CustomAddForm } from '../../forms/CustomAddForm';
import { ConfirmationDialogue } from '../../forms/ConfirmationDialogue';
import { CalendarPicker, useCloseOnOutsidePress } from './calendar';

const READ_ONLY_CELL_HINT = 'Read-only — this column cannot be edited';

/** Prefer explicit overrides including null (cleared cell); `??` would wrongly fall through on null. */
function rawWithRowOverrides(rowKey, columnKey, overrides, fallbackFromItem) {
    const rowOv = overrides?.[rowKey];
    if (rowOv != null && Object.prototype.hasOwnProperty.call(rowOv, columnKey)) {
        return rowOv[columnKey];
    }
    return fallbackFromItem;
}

/** Map stored/raw select value to an entry in opts so <select value> matches a real <option value> (e.g. OutOfOrder → Out Of Order). */
function resolveSelectValueForControl(rawValue, opts, interpreter) {
    if (rawValue === '' || rawValue == null) return '';
    const strRaw = String(rawValue);
    if (opts.some((o) => String(o) === strRaw)) return strRaw;
    if (typeof interpreter === 'function') {
        const dispRaw = interpreter(strRaw);
        const matchCanon = opts.find((o) => interpreter(String(o)) === dispRaw);
        if (matchCanon !== undefined) return String(matchCanon);
        const matchLabel = opts.find((o) => String(o) === String(dispRaw));
        if (matchLabel !== undefined) return String(matchLabel);
    }
    return strRaw;
}

function getInputValue(item, column, editState, rowKey, defaultValueByColumn, skipInterpreter, overrides) {
    const cellKey = `${rowKey}|${column.id ?? column.valueKey}`;
    const columnKey = column.valueKey ?? column.id;
    if (editState.hasOwnProperty(cellKey)) return editState[cellKey];
    let val = rawWithRowOverrides(rowKey, columnKey, overrides, column.valueKey != null ? item[column.valueKey] : item.customTableData?.[column.id]);
    const interpreter = defaultValueByColumn?.[column.valueKey]?.interpreter ?? defaultValueByColumn?.[column.id]?.interpreter;
    if (!skipInterpreter && val != null && (column.valueKey != null || column.id != null) && typeof interpreter === 'function') {
        val = interpreter(val);
    }
    return val != null && val !== "" ? String(val) : "";
}
const buildColumnEditForm = (column, { broadCastMessage, setColumns }) => ({
    columns: [
        {
            label: "Name",
            name: "label",
            autoComplete: "off",
            mandatory: true,
            defaultValue: () => column?.label ?? "",
        },
        column.type === "date" && column.dateAlert ? {
            label: column.dateAlert === "daysBefore" ? "Days Before" : "Days After",
            name: column.dateAlert,
            mandatory: true,
            autoComplete: "off",
            processBeforeSending: (val) => {
                return parseInt(val);
            },
            filters:[
                (val)=>{
                    return val.replace(/[^0-9]/g,"");
                }
            ],
            defaultValue: () => column[column.dateAlert] ?? "",
        } : null,
        {
            type: 'customButton',
            buttonLabel: 'Remove Column',
            bgColor: 'rgba(var(--lightAlertRGB), 0.7)',
            action: (postAction) => {
                addPopup({
                    content: ConfirmationDialogue,
                    popupData: {
                        action: (postResultAction) => {
                            broadCastMessage?.({ structuralChange: true, action: 'remove', columnId: column.id});
                            setColumns(prev => prev.filter(c => (c.id ?? c.valueKey) !== (column.id ?? column.valueKey)));
                            postResultAction(true);
                            postAction();
                        },
                        name: "Confirm",
                        formTitle: "Remove Column",
                        submitTitle: "Remove",
                        method: "PATCH",
                        message: "Are you sure you want to permanently remove the column and all its data? This action cannot be undone."
                    },
                    postSuccessAction: () => {},
                    EZCloseAllowed: true
                });
            }
        }
    ].filter(Boolean),
});
const COLUMN_ADD_FORM = {
    columns: [
        {
            hiddenField:true,
            name:"id",
            defaultValue:()=>{
                return generateShortId();
            }
        },
        {
            label: "Name",
            name: "label",
            autoComplete: "off",
            mandatory: true,
        },
        {
            label: "Type",
            name: "type",
            type: "select",
            dataset: ["Text", "Date", "Selectable Options"],
            onChange: val => {
                function changeInputs(formTarget, currentlySelected){
                    const allFields = ["options", "dateAlert"];
                    let availableFieldNames = [];
                    if(currentlySelected === "Selectable Options"){
                        availableFieldNames = ["options"];
                    }else if(currentlySelected === "Date"){
                        availableFieldNames = ["dateAlert"];
                    }else{
                        availableFieldNames = [];
                    }
                    for(let fieldName of allFields){
                        let inputField = formTarget.querySelector(`input[name="${fieldName}"]`);
                        if(!inputField){
                            inputField = formTarget.querySelector(`select[name="${fieldName}"]`);
                        }
                        inputField.disabled = !availableFieldNames.includes(fieldName);
                        inputField.parentNode.style.display = availableFieldNames.includes(fieldName) ? "block" : "none";
                    }
                }
                
                let target = val.target; 
                while (target.tagName !== "FORM" ){
                    target = target.parentNode;
                }
                changeInputs(target, val.target.value);
                
            },
        },
        {
            label: "Options (separated by commas)",
            name: "options",
            disabled: true,
            mandatory: true,
            autoComplete:'off',
            customDisplayMode : "none",
        },
        {
            label: "Conditional Alert",
            name: "dateAlert",
            disabled: true,
            customDisplayMode : "none",
            type: "select",
            dataset: ["", "Days Before", "Days After"],
            processBeforeSending: (val) => {
                return val === "" ? null : val === "Days Before" ? "daysBefore" : "daysAfter";
            },
            onChange: val => {
                function changeInputs(formTarget, currentlySelected){
                    const allFields = ["daysBefore", "daysAfter"];
                    let availableFieldNames = [];
                    if(currentlySelected === "Days Before"){
                        availableFieldNames = ["daysBefore"];
                    }else if(currentlySelected === "Days After"){
                        availableFieldNames = ["daysAfter"];
                    }else{
                        availableFieldNames = [];
                    }
                    for(let fieldName of allFields){
                        let inputField = formTarget.querySelector(`input[name="${fieldName}"]`)
                        inputField.disabled = !availableFieldNames.includes(fieldName);
                        inputField.parentNode.style.display = availableFieldNames.includes(fieldName) ? "block" : "none";
                    }
                }
                
                let target = val.target; 
                while (target.tagName !== "FORM" ){
                    target = target.parentNode;
                }
                changeInputs(target, val.target.value);
                
            },
        },
        {
            label: "Days Before",
            name: "daysBefore",
            disabled: true,
            mandatory: true,
            autoComplete: "off",
            customDisplayMode : "none",
            processBeforeSending: (val) => {
                return parseInt(val);
            },
            filters:[
                (val)=>{
                    return val.replace(/[^0-9]/g,"");
                }
            ],
        },
        {
            label: "Days After",
            name: "daysAfter",
            disabled: true,
            mandatory: true,
            autoComplete: "off",
            customDisplayMode : "none",
            processBeforeSending: (val) => {
                return parseInt(val);
            },
            filters:[
                (val)=>{
                    return val.replace(/[^0-9]/g,"");
                }
            ],
        },
    ]
}

const INVALID_DATE_DISPLAY_MS = 1000;

function stripTableCalendarDay(d) {
	return new Date(d.getFullYear(), d.getMonth(), d.getDate());
}

function formatTableCalendarPick(d) {
	if (!d) return '';
	const t = stripTableCalendarDay(d);
	const y = t.getFullYear();
	const mo = t.getMonth() + 1;
	const day = t.getDate();
	const intermediate = `${y}-${String(mo).padStart(2, '0')}-${String(day).padStart(2, '0')} 12:00 AM`;
	const { date } = dateFormatter(intermediate);
	if (date) return date;
	return `${String(mo).padStart(2, '0')}/${String(day).padStart(2, '0')}/${String(y).slice(-2)}`;
}

function parseTableCalendarFragment(fragment) {
	if (!fragment || !String(fragment).trim()) return null;
	const { date, time } = dateFormatter(fragment.trim());
	if (!date) return null;
	const dm = date.match(/^(\d{2})\/(\d{2})\/(\d{2})$/);
	if (!dm) return null;
	const [, mo, d, yy] = dm;
	const year = 2000 + parseInt(yy, 10);
	if (!time) {
		return new Date(year, parseInt(mo, 10) - 1, parseInt(d, 10), 0, 0);
	}
	const tm = time.match(/^(\d{1,2}):(\d{2})\s*(AM|PM)$/i);
	if (!tm) return null;
	let h = parseInt(tm[1], 10);
	const mi = parseInt(tm[2], 10);
	const ap = tm[3].toUpperCase();
	if (ap === 'PM' && h !== 12) h += 12;
	if (ap === 'AM' && h === 12) h = 0;
	return new Date(year, parseInt(mo, 10) - 1, parseInt(d, 10), h, mi);
}

function parseTableDateCellInitialStart(raw) {
	if (!raw || !String(raw).trim()) return null;
	const parsed = parseTableCalendarFragment(String(raw).trim());
	return parsed ? stripTableCalendarDay(parsed) : null;
}

function parseDateForSort(val) {
	if (val == null || val === '') return null;
	if (val instanceof Date) return val.getTime();
	if (typeof val === 'object' && val !== null) {
		const y = val.year ?? val.yearValue;
		const m = val.month ?? val.monthValue;
		const d = val.day ?? val.dayOfMonth;
		if (y != null && m != null && d != null) {
			return new Date(y, m - 1, d).getTime();
		}
		return NaN;
	}
	const str = String(val).trim();
	if (!str) return null;
	// YYYY-MM-DD (Java LocalDate)
	const isoMatch = str.match(/\d{4}-\d{2}-\d{2}/);
	if (isoMatch) {
		const ts = new Date(isoMatch[0]).getTime();
		if (!Number.isNaN(ts)) return ts;
	}
	// MM/dd/yy or MM/dd/yyyy
	const parts = str.split('/').map(Number);
	if (parts.length >= 3 && parts[0] && parts[1] && parts[2]) {
		const [m, d, y] = parts;
		const fullYear = y >= 100 ? y : (y < 50 ? 2000 + y : 1900 + y);
		return new Date(fullYear, m - 1, d).getTime();
	}
	return NaN;
}

function getFormattedValue(column, rawVal, defaultValueByColumn) {
	const formatter = defaultValueByColumn?.[column.valueKey]?.formatter ?? defaultValueByColumn?.[column.id]?.formatter;
	return typeof formatter === 'function' ? formatter(rawVal) : rawVal;
}

function getRawValueForDate(item, column, rowKey, overrides, editState) {
	const cellKey = `${rowKey}|${column.id ?? column.valueKey}`;
	const columnKey = column.valueKey ?? column.id;
	if (editState?.hasOwnProperty(cellKey)) {
		const val = editState[cellKey];
		return val != null && val !== '' ? val : null;
	}
	const raw = rawWithRowOverrides(rowKey, columnKey, overrides, column.valueKey != null ? item[column.valueKey] : item.customTableData?.[column.id]);
	return raw != null && raw !== '' ? raw : null;
}

function isDateInAlertRange(rawVal, col, currentTime) {
	if (!rawVal || col?.dateAlert == null) return false;
	const daysBefore = col.dateAlert === 'daysBefore' ? col.daysBefore : null;
	const daysAfter = col.dateAlert === 'daysAfter' ? col.daysAfter : null;
	if (daysBefore == null && daysAfter == null) return false;

	const cellTs = parseDateForSort(rawVal);
	if (cellTs == null || Number.isNaN(cellTs)) return false;

	const nowMs = currentTime ? new Date(currentTime).getTime() : Date.now();
	if (Number.isNaN(nowMs)) return false;

	const cellDate = new Date(cellTs);
	const cellStartOfDay = new Date(cellDate.getFullYear(), cellDate.getMonth(), cellDate.getDate()).getTime();
	const nowDate = new Date(nowMs);
	const nowStartOfDay = new Date(nowDate.getFullYear(), nowDate.getMonth(), nowDate.getDate()).getTime();

	const msPerDay = 24 * 60 * 60 * 1000;
	const daysBetween = (cellStartOfDay - nowStartOfDay) / msPerDay;

	// daysBefore: warn when X days left until the date, and keep warning if the date has passed (overdue)
	// Alert when: date is today/past (overdue) OR date is in the future within X days
	if (daysBefore != null && typeof daysBefore === 'number') {
		if (daysBetween <= 0 || daysBetween <= daysBefore) return true;
	}
	// daysAfter: date is in the past, at least X days have passed since that date
	if (daysAfter != null && typeof daysAfter === 'number') {
		if (daysBetween <= -daysAfter) return true;
	}
	return false;
}

function linkifyText(text) {
	if (!text || typeof text !== 'string') return text;
	const urlRegex = /(https?:\/\/[^\s]+|www\.[^\s]+)/gi;
	const parts = text.split(urlRegex);
	if (parts.length <= 1) return text;
	return parts.map((part, i) => {
		const isUrl = /^https?:\/\//i.test(part) || /^www\./i.test(part);
		if (isUrl) {
			let href = part;
			if (!/^https?:\/\//i.test(href)) href = 'https://' + href;
			return (
				<a
					key={i}
					href={href}
					target="_blank"
					rel="noopener noreferrer"
					onClick={(e) => e.stopPropagation()}
					className={styles.cellLink}
				>
					{part}
				</a>
			);
		}
		return part;
	});
}

function broadcastCellChange(broadCastMessage, rowKey, column, newValue, defaultValueByColumn, cellValuesRef) {
	if (!broadCastMessage || newValue === undefined) return;
	if (column?.editable === false) return;
	const cellKey = `${rowKey}|${column.id ?? column.valueKey}`;
	const formatter = defaultValueByColumn?.[column.valueKey]?.formatter ?? defaultValueByColumn?.[column.id]?.formatter;
	const valueToSend = typeof formatter === 'function' ? formatter(newValue) : newValue;
	const currentTracked = cellValuesRef?.current?.[cellKey];
	if (currentTracked === valueToSend) return;
	const key = column.valueKey ?? column.id;
	broadCastMessage({ id: rowKey, key, value: valueToSend });
	if (cellValuesRef?.current) cellValuesRef.current[cellKey] = valueToSend;
}

const TRAILER_TELEMETRY_SHAPE_KEYS = ['lastLocation', 'lastUpdated', 'currentTruck', 'currentTruckNumber', 'potentiallyDetached', 'active', 'manuallyRefreshable'];

function entryLooksLikeTrailerTelemetry(v) {
	return v != null && typeof v === 'object' && !Array.isArray(v)
		&& TRAILER_TELEMETRY_SHAPE_KEYS.some((k) => Object.prototype.hasOwnProperty.call(v, k));
}

function isTrailerTelemetryPayload(parsed) {
	if (!parsed || typeof parsed !== 'object') return false;
	const keys = Object.keys(parsed);
	if (keys.length === 0) return false;
	for (const k of keys) {
		if (!entryLooksLikeTrailerTelemetry(parsed[k])) return false;
	}
	return true;
}

function normalizeTruckForCompare(val) {
	if (val == null || val === '') return null;
	return String(val);
}

function effectiveTrailerField(overrides, rowKey, field, item) {
	const o = overrides?.[rowKey]?.[field];
	if (o !== undefined) return o;
	return item?.[field];
}

/** Second argument gives row context so columns can depend on other fields (e.g. currentTruck vs potentiallyDetached). */
function getConditionalFormattingColor(column, cellDisplayValue, defaultValueByColumn, ctx) {
	const fn = defaultValueByColumn?.[column.valueKey]?.conditionalFormatting ?? defaultValueByColumn?.[column.id]?.conditionalFormatting;
	if (typeof fn !== 'function') return null;
	try {
		const out = fn(cellDisplayValue, ctx);
		return out != null && out !== '' ? String(out) : null;
	} catch {
		return null;
	}
}

function conditionalFormattingStyle(color) {
	return color ? { color, fontWeight: 700 } : undefined;
}

function TableDateCellWithCalendar({
	cellKey,
	rowKey,
	col,
	displayValue,
	isInvalidDate,
	isDateAlert,
	setInvalidDateCell,
	setEditState,
	broadCastMessage,
	defaultValueByColumn,
	cellValuesRef,
	cellAlertCacheRef,
	editable = true,
	conditionalStyle,
}) {
	const inputRef = useRef(null);
	const shellRef = useRef(null);
	const popoverRef = useRef(null);
	const [calendarOpen, setCalendarOpen] = useState(false);
	const outsideRefs = useMemo(() => [shellRef, popoverRef], []);
	useCloseOnOutsidePress(calendarOpen, outsideRefs, () => setCalendarOpen(false));

	const rawForCalendar = isInvalidDate ? '' : displayValue;
	const initialStart = parseTableDateCellInitialStart(rawForCalendar);

	const commitDateBlur = (rawVal) => {
		if (!editable) return;
		const val = rawVal?.trim?.() ?? '';
		delete cellAlertCacheRef.current[cellKey];
		if (!val) {
			setInvalidDateCell((prev) => (prev === cellKey ? null : prev));
			setEditState((prev) => ({ ...prev, [cellKey]: '' }));
			broadcastCellChange(broadCastMessage, rowKey, col, '', defaultValueByColumn, cellValuesRef);
			return;
		}
		const result = dateFormatter(val);
		if (result.date === null) {
			setInvalidDateCell(cellKey);
			setEditState((prev) => ({ ...prev, [cellKey]: '' }));
			setTimeout(() => setInvalidDateCell(null), INVALID_DATE_DISPLAY_MS);
		} else {
			setInvalidDateCell((prev) => (prev === cellKey ? null : prev));
			setEditState((prev) => ({ ...prev, [cellKey]: result.date }));
			broadcastCellChange(broadCastMessage, rowKey, col, result.date, defaultValueByColumn, cellValuesRef);
		}
	};

	const handleBlur = (e) => {
		if (!editable) return;
		const rt = e.relatedTarget;
		if (popoverRef.current && rt && popoverRef.current.contains(rt)) {
			return;
		}
		setCalendarOpen(false);
		commitDateBlur(e.target.value);
	};

	const applyFromCalendar = (d) => {
		if (!editable) return;
		const dateStr = formatTableCalendarPick(d);
		delete cellAlertCacheRef.current[cellKey];
		setInvalidDateCell((prev) => (prev === cellKey ? null : prev));
		setEditState((prev) => ({ ...prev, [cellKey]: dateStr }));
		broadcastCellChange(broadCastMessage, rowKey, col, dateStr, defaultValueByColumn, cellValuesRef);
		setCalendarOpen(false);
		requestAnimationFrame(() => {
			inputRef.current?.blur();
		});
	};

	return (
		<div ref={shellRef} className={styles.dateCellWithCalendar}>
			<input
				ref={inputRef}
				type="text"
				readOnly={!editable}
				tabIndex={editable ? undefined : -1}
				className={`${styles.cellInput} ${!editable ? styles.cellReadOnly : ''} ${isInvalidDate ? styles.invalidDate : ''} ${isDateAlert ? styles.dateAlert : ''}`}
				style={conditionalStyle && !isInvalidDate && !isDateAlert ? conditionalStyle : undefined}
				value={isInvalidDate ? '' : displayValue}
				placeholder={isInvalidDate ? 'Invalid date' : undefined}
				onFocus={() => {
					if (editable) setCalendarOpen(true);
				}}
				onChange={(e) => {
					if (!editable) return;
					setInvalidDateCell((prev) => (prev === cellKey ? null : prev));
					const val = e.target.value;
					setEditState((prev) => ({ ...prev, [cellKey]: val }));
				}}
				onBlur={editable ? handleBlur : undefined}
				autoComplete="off"
			/>
			{editable && calendarOpen ? (
				<CalendarPicker
					key={`${rawForCalendar}-${calendarOpen}`}
					selectType="singleDate"
					popoverAlign="center"
					anchorRef={shellRef}
					popoverRef={popoverRef}
					autoVerticalPlacement
					initialStart={initialStart || undefined}
					onCancel={() => setCalendarOpen(false)}
					onApply={applyFromCalendar}
				/>
			) : null}
		</div>
	);
}

export const CustomDataTable = ({ itemCategory, itemsArray, tableConf, identifierColumnName, identifierLabel, setCustomTableUpdateRef, broadCastMessage, defaultValueByColumn, bringToTopRef, currentTime, excludeRow }) => {
    const [editState, setEditState] = useState({});
    const [invalidDateCell, setInvalidDateCell] = useState(null);
    const [overrides, setOverrides] = useState({});
    const [editingTextCellKey, setEditingTextCellKey] = useState(null);
    const cellValuesRef = useRef({});
    const textCellHeightsRef = useRef({});
    const editingTextareaRef = useRef(null);
    const cellAlertCacheRef = useRef({});
    const [columnWidths, setColumnWidths] = useState({});
    const [sortBy, setSortBy] = useState(null);
    const [sortDirection, setSortDirection] = useState('asc');
    const [bringToTopOrder, setBringToTopOrder] = useState([]);
    const [highlightedRowId, setHighlightedRowId] = useState(null);
    const highlightTimeoutRef = useRef(null);
    const [identifierFilter, setIdentifierFilter] = useState('');
    const theadRef = useRef(null);
    const resizeRef = useRef({ colId: null, startX: 0, startWidth: 0, lastWidth: null });
    const itemsArrayRef = useRef(itemsArray);
    const overridesRef = useRef(overrides);
    itemsArrayRef.current = itemsArray;
    overridesRef.current = overrides;

    const visibleItems = useMemo(() => {
        const arr = Array.isArray(itemsArray) ? [...itemsArray] : [];
        if (typeof excludeRow !== 'function') return arr;
        return arr.filter((item) => !excludeRow(item));
    }, [itemsArray, excludeRow]);

    const handleSort = useCallback((colIndex) => {
        setBringToTopOrder([]);
        setSortBy(prev => {
            if (prev === colIndex) {
                setSortDirection(d => d === 'asc' ? 'desc' : 'asc');
            } else {
                setSortDirection('asc');
            }
            return colIndex;
        });
    }, []);

    const sortedItems = useMemo(() => {
        const arr = visibleItems.length === 0 ? [] : [...visibleItems];
        if (arr.length === 0 || sortBy === null) return arr;
        const cols = tableConf?.structure?.columns ?? tableConf?.columns ?? [];
        const dir = sortDirection === 'asc' ? 1 : -1;

        const getSortValue = (item, col, key) => {
            const rowKey = item.id ?? item[identifierColumnName];
            const cellKey = `${rowKey}|${key}`;
            const committedVal = cellValuesRef?.current?.[cellKey];
            if (committedVal !== undefined) return committedVal;
            const overrideVal = overrides?.[rowKey]?.[key];
            if (overrideVal !== undefined) return overrideVal;
            return col.valueKey != null ? item[col.valueKey] : item.customTableData?.[col.id];
        };

        return arr.sort((a, b) => {
            let aVal, bVal;
            if (sortBy === -1) {
                aVal = a[identifierColumnName] ?? '';
                bVal = b[identifierColumnName] ?? '';
                return dir * String(aVal).localeCompare(String(bVal), undefined, { numeric: true });
            }
            const col = cols[sortBy];
            if (!col) return 0;
            const colType = col.type?.toLowerCase?.() ?? 'text';
            const key = col.valueKey ?? col.id;
            aVal = getSortValue(a, col, key);
            bVal = getSortValue(b, col, key);
            if (colType === 'date') {
                const aTs = parseDateForSort(aVal);
                const bTs = parseDateForSort(bVal);
                const aInvalid = aTs == null || Number.isNaN(aTs);
                const bInvalid = bTs == null || Number.isNaN(bTs);
                if (aInvalid && bInvalid) return 0;
                if (aInvalid) return dir;
                if (bInvalid) return -dir;
                return dir * (aTs - bTs);
            }
            aVal = aVal != null ? String(aVal) : '';
            bVal = bVal != null ? String(bVal) : '';
            return dir * aVal.localeCompare(bVal, undefined, { numeric: true });
        });
    }, [visibleItems, sortBy, sortDirection, identifierColumnName, tableConf?.structure?.columns, tableConf?.columns, overrides]);

    const filteredItems = useMemo(() => {
        const trim = identifierFilter.trim().toLowerCase();
        if (!trim) return sortedItems;
        return sortedItems.filter(item => {
            const idVal = item[identifierColumnName];
            return idVal != null && String(idVal).toLowerCase().includes(trim);
        });
    }, [sortedItems, identifierFilter, identifierColumnName]);

    const displayedItems = useMemo(() => {
        if (!bringToTopOrder?.length) return filteredItems;
        const topIds = new Set(bringToTopOrder.map(id => String(id)));
        const topItems = bringToTopOrder
            .map(id => filteredItems.find(item => String(item[identifierColumnName] ?? item.id ?? '') === String(id)))
            .filter(Boolean);
        const rest = filteredItems.filter(item => !topIds.has(String(item[identifierColumnName] ?? item.id ?? '')));
        return [...topItems, ...rest];
    }, [filteredItems, bringToTopOrder, identifierColumnName]);

    useEffect(() => {
        if (!bringToTopRef) return;
        bringToTopRef.current = (identifierValue) => {
            if (identifierValue == null || identifierValue === '') return;
            setSortBy(null);
            setBringToTopOrder(prev => {
                const str = String(identifierValue);
                const without = prev.filter(id => String(id) !== str);
                return [identifierValue, ...without];
            });
            setHighlightedRowId(identifierValue);
            if (highlightTimeoutRef.current) clearTimeout(highlightTimeoutRef.current);
            highlightTimeoutRef.current = setTimeout(() => {
                setHighlightedRowId(null);
                highlightTimeoutRef.current = null;
            }, 1000);
        };
        return () => {
            bringToTopRef.current = null;
            if (highlightTimeoutRef.current) clearTimeout(highlightTimeoutRef.current);
        };
    }, [bringToTopRef]);

    useEffect(() => {
        if (!setCustomTableUpdateRef) return;
        setCustomTableUpdateRef.current = (payload) => {
            if (!payload || typeof payload !== 'object') return;
            if (payload.structuralChange) {
                const { action, column, columns: newColumns, key, value, columnId } = payload;
                const clearCacheForDateColumn = (col) => {
                    if (!col) return;
                    const colType = col.type?.toLowerCase?.() ?? 'text';
                    if (colType === 'date' && col.dateAlert) {
                        const colId = col.id ?? col.valueKey;
                        const cache = cellAlertCacheRef.current;
                        for (const cacheKey of Object.keys(cache)) {
                            if (cacheKey.endsWith(`|${colId}`)) delete cache[cacheKey];
                        }
                    }
                };
                if (action === 'add' && column) {
                    cellAlertCacheRef.current = {};
                    setColumns(prev => [...prev, column]);
                } else if (action === 'remove' && column) {
                    clearCacheForDateColumn(column);
                    setColumns(prev => prev.filter(c => (c.id ?? c.valueKey) !== (column.id ?? column.valueKey)));
                } else if (action === 'edit' && column) {
                    clearCacheForDateColumn(column);
                    setColumns(prev => prev.map(c => (c.id === column.id ? { ...c, ...column } : c)));
                } else if (action === 'reorder' && Array.isArray(newColumns) && newColumns.length > 0) {
                    cellAlertCacheRef.current = {};
                    setColumns(newColumns);
                } else if (key === 'width' && value) {
                    const colId = columnId ?? column?.id;
                    if (colId) {
                        setColumnWidths(prev => ({ ...prev, [colId]: value }));
                        setColumns(prev => prev.map(c => ((c.id ?? c.valueKey) === colId ? { ...c, width: value } : c)));
                    }
                }
            } else if (isTrailerTelemetryPayload(payload)) {
                const items = itemsArrayRef.current;
                if (!Array.isArray(items)) return;
                const prevOverrides = overridesRef.current ?? {};
                const rowPatches = [];
                for (const [trailerNum, loc] of Object.entries(payload)) {
                    if (!entryLooksLikeTrailerTelemetry(loc)) continue;
                    const item = items.find((it) => String(it[identifierColumnName] ?? '') === String(trailerNum));
                    if (!item) continue;
                    const rowKey = item.id ?? item[identifierColumnName];
                    if (rowKey == null) continue;
                    const patch = {};
                    // The table column `currentTruck` is the Trailer entity's String truckNumber.
                    // Websocket telemetry now sends `currentTruck` (Integer truckId) plus
                    // `currentTruckNumber` (String). Use the String form for display overrides,
                    // falling back to the legacy field if a producer hasn't been migrated yet.
                    if (Object.prototype.hasOwnProperty.call(loc, 'currentTruckNumber')
                            || Object.prototype.hasOwnProperty.call(loc, 'currentTruck')) {
                        const incoming = Object.prototype.hasOwnProperty.call(loc, 'currentTruckNumber')
                            ? (loc.currentTruckNumber ?? null)
                            : (loc.currentTruck ?? null);
                        const eff = effectiveTrailerField(prevOverrides, rowKey, 'currentTruck', item);
                        if (normalizeTruckForCompare(incoming) !== normalizeTruckForCompare(eff)) {
                            patch.currentTruck = incoming;
                        }
                    }
                    if (Object.prototype.hasOwnProperty.call(loc, 'potentiallyDetached')) {
                        const incoming = !!loc.potentiallyDetached;
                        const eff = effectiveTrailerField(prevOverrides, rowKey, 'potentiallyDetached', item);
                        if (!!eff !== incoming) {
                            patch.potentiallyDetached = incoming;
                        }
                    }
                    if (Object.keys(patch).length > 0) {
                        rowPatches.push({ rowKey, patch });
                    }
                }
                if (rowPatches.length === 0) return;
                const cellKeysToClear = [];
                for (const { rowKey, patch } of rowPatches) {
                    for (const field of Object.keys(patch)) {
                        const cellKey = `${rowKey}|${field}`;
                        cellKeysToClear.push(cellKey);
                        delete cellAlertCacheRef.current[cellKey];
                        if (cellValuesRef.current) cellValuesRef.current[cellKey] = patch[field];
                    }
                }
                setOverrides(prev => {
                    let next = prev;
                    for (const { rowKey, patch } of rowPatches) {
                        next = { ...next, [rowKey]: { ...next[rowKey], ...patch } };
                    }
                    return next;
                });
                setEditState(prev => {
                    const next = { ...prev };
                    for (const cellKey of cellKeysToClear) {
                        delete next[cellKey];
                    }
                    return next;
                });
                setEditingTextCellKey(prev => (prev != null && cellKeysToClear.includes(prev) ? null : prev));
            } else {
                const { id, key, value } = payload;
                if (id == null || key == null) return;
                const cellKey = `${id}|${key}`;
                delete cellAlertCacheRef.current[cellKey];
                setOverrides(prev => ({ ...prev, [id]: { ...prev[id], [key]: value } }));
                setEditState(prev => {
                    const next = { ...prev };
                    delete next[cellKey];
                    return next;
                });
                setEditingTextCellKey(prev => (prev === cellKey ? null : prev));
                if (cellValuesRef.current) cellValuesRef.current[cellKey] = value;
            }
        };
        return () => { setCustomTableUpdateRef.current = null; };
    }, [setCustomTableUpdateRef, identifierColumnName]);
    const columnsSource = tableConf?.structure?.columns ?? tableConf?.columns;
    const [columns, setColumns] = useState(() => Array.isArray(columnsSource) ? columnsSource : []);

    const columnAlertFlags = useMemo(() => {
        const flags = {};
        const cache = cellAlertCacheRef.current;
        if (!Array.isArray(columns) || !displayedItems?.length) return flags;
        for (const col of columns) {
            const colId = col.id ?? col.valueKey;
            const colType = col.type?.toLowerCase?.() ?? 'text';
            if (colType !== 'date' || !col.dateAlert) continue;
            for (const item of displayedItems) {
                const rowKey = item.id ?? item[identifierColumnName] ?? '';
                const cellKey = `${rowKey}|${colId}`;
                let isAlert = cache[cellKey];
                if (isAlert === undefined) {
                    const rawVal = getRawValueForDate(item, col, rowKey, overrides, editState);
                    isAlert = !!(rawVal && isDateInAlertRange(rawVal, col, currentTime));
                    cache[cellKey] = isAlert;
                }
                if (isAlert) {
                    flags[colId] = true;
                    break;
                }
            }
        }
        return flags;
    }, [displayedItems, columns, overrides, editState, currentTime, identifierColumnName]);

    useEffect(() => {
        const source = tableConf?.structure?.columns ?? tableConf?.columns;
        const incoming = Array.isArray(source) ? source : [];
        cellAlertCacheRef.current = {};
        setColumns(incoming);
        const widths = {};
        for (const c of incoming) {
            const id = c.id ?? c.valueKey;
            if (id && c.width) widths[id] = c.width;
        }
        if (Object.keys(widths).length > 0) setColumnWidths(prev => ({ ...prev, ...widths }));
    }, [tableConf?.structure?.columns, tableConf?.columns]);

    useEffect(() => {
        const tr = theadRef.current?.querySelector('tr');
        if (!tr || !broadCastMessage) return;
        const headerCells = Array.from(tr.children).slice(1);
        headerCells.forEach((th, colIndex) => {
            th.draggable = false;
            th.onmousedown = (e) => {
                if (e.target.closest(`.${styles.dragger}`)) {
                    th.dataset.dragFromHandle = '1';
                    th.draggable = true;
                } else {
                    th.dataset.dragFromHandle = '';
                }
            };
            th.onmouseup = th.onmouseleave = () => {
                delete th.dataset.dragFromHandle;
                th.draggable = false;
            };
            th.ondragstart = (e) => {
                if (!th.dataset.dragFromHandle) {
                    e.preventDefault();
                    return;
                }
                e.dataTransfer.setData('text/plain', String(colIndex));
                e.dataTransfer.effectAllowed = 'move';
                th.classList.add(styles.dragging);
            };
            th.ondragend = () => {
                delete th.dataset.dragFromHandle;
                th.draggable = false;
                th.classList.remove(styles.dragging);
                headerCells.forEach(c => c.classList.remove(styles.dragOver));
            };
            th.ondragover = (e) => {
                e.preventDefault();
                e.dataTransfer.dropEffect = 'move';
                th.classList.add(styles.dragOver);
            };
            th.ondragleave = () => th.classList.remove(styles.dragOver);
            th.ondrop = (e) => {
                e.preventDefault();
                th.classList.remove(styles.dragOver);
                const fromIdx = parseInt(e.dataTransfer.getData('text/plain'), 10);
                const toIdx = colIndex;
                if (fromIdx === toIdx || Number.isNaN(fromIdx)) return;
                setColumns(prev => {
                    const reordered = [...prev];
                    const [removed] = reordered.splice(fromIdx, 1);
                    reordered.splice(toIdx, 0, removed);
                    broadCastMessage({ structuralChange: true, action: 'reorder', columns: reordered });
                    return reordered;
                });
            };
        });
        return () => {
            headerCells.forEach(th => {
                th.onmousedown = null;
                th.onmouseup = null;
                th.onmouseleave = null;
                th.ondragstart = null;
                th.ondragend = null;
                th.ondragover = null;
                th.ondragleave = null;
                th.ondrop = null;
            });
        };
    }, [columns, broadCastMessage]);

    useEffect(() => {
        const newMap = {};
        const cols = tableConf?.structure?.columns ?? tableConf?.columns ?? [];
        const arr = visibleItems.length === 0 ? [] : visibleItems;
        for (const item of arr) {
            const rowKey = item.id ?? item[identifierColumnName];
            if (rowKey == null) continue;
            for (const col of cols) {
                const cellKey = `${rowKey}|${col.id ?? col.valueKey}`;
                const key = col.valueKey ?? col.id;
                const rawVal = rawWithRowOverrides(rowKey, key, overrides, col.valueKey != null ? item[col.valueKey] : item.customTableData?.[col.id]);
                newMap[cellKey] = getFormattedValue(col, rawVal, defaultValueByColumn);
            }
        }
        cellValuesRef.current = newMap;
    }, [visibleItems, tableConf?.structure?.columns, tableConf?.columns, identifierColumnName, defaultValueByColumn, overrides]);

    const headerLabel = identifierLabel ?? (identifierColumnName ? String(identifierColumnName).replace(/([A-Z])/g, " $1").replace(/^./, (s) => s.toUpperCase()).trim() : "Item");

    const handleResizeStart = useCallback((e, colId, currentWidth) => {
        e.preventDefault();
        e.stopPropagation();
        const startWidth = currentWidth ? parseInt(String(currentWidth).replace('px', ''), 10) : 120;
        resizeRef.current = { colId, startX: e.clientX, startWidth: Number.isNaN(startWidth) ? 120 : startWidth, lastWidth: currentWidth };
        const onMove = (ev) => {
            ev.preventDefault();
            const r = resizeRef.current;
            if (!r.colId) return;
            const delta = ev.clientX - r.startX;
            const newWidthPx = Math.max(40, r.startWidth + delta);
            const widthStr = `${newWidthPx}px`;
            r.lastWidth = widthStr;
            setColumnWidths(prev => ({ ...prev, [r.colId]: widthStr }));
        };
        const onUp = () => {
            document.body.style.userSelect = '';
            document.body.style.cursor = '';
            const r = resizeRef.current;
            if (r.colId && r.lastWidth) {
                broadCastMessage?.({ structuralChange: true, key: 'width', value: r.lastWidth, columnId: r.colId });
            }
            resizeRef.current = { colId: null, startX: 0, startWidth: 0, lastWidth: null };
            document.removeEventListener('mousemove', onMove);
            document.removeEventListener('mouseup', onUp);
        };
        document.body.style.userSelect = 'none';
        document.body.style.cursor = 'col-resize';
        document.addEventListener('mousemove', onMove);
        document.addEventListener('mouseup', onUp);
    }, [broadCastMessage]);

    const headerCellCount = 1 + columns.length;
    const singleColumn = columns.length === 0;
    const tableEl = (
        <div className={`${styles.customDataTable} ${singleColumn ? styles.fitContent : ""} ${styles.tableInRow}`}>
            <table className={`${styles.table} ${singleColumn ? styles.fitContent : ""}`}>
                <thead ref={theadRef}>
                    <tr>
                        <th
                            className={`${styles.identifierHeader} ${styles.sortableHeader}`}
                            onClick={() => handleSort(-1)}
                            title="Sort"
                        >
                            {headerLabel}
                            {sortBy === -1 && (sortDirection === 'asc' ? <HiArrowNarrowUp className={styles.sortIcon} /> : <HiArrowNarrowDown className={styles.sortIcon} />)}
                        </th>
                        {columns.map((col, colIndex) => {
                            const colId = col.id ?? col.valueKey;
                            const colWidth = columnWidths[colId] ?? col.width;
                            return (
                                <th
                                    key={colId ?? colIndex}
                                    className={`${styles.headerCellWithBorder} ${styles.sortableHeader}`}
                                    style={colWidth ? { width: colWidth, minWidth: colWidth } : undefined}
                                    onClick={(e) => !e.target.closest(`.${styles.resizeHandle}`) && !e.target.closest(`.${styles.dragger}`) && !e.target.closest(`.${styles.editColumnBtn}`) && handleSort(colIndex)}
                                    title="Sort"
                                >
                                    <div
                                        className={styles.resizeHandle}
                                        onMouseDown={(e) => handleResizeStart(e, colId, colWidth)}
                                        title="Drag to resize"
                                    />
                                    <div className={styles.columnHeaderWithEdit}>
                                        <span className={styles.dragger} title="Drag to reorder">
                                            <MdDragIndicator />
                                        </span>
                                        <span className={styles.columnHeaderLabel}>
                                            {col.label}
                                            {columnAlertFlags[colId] && <FaTriangleExclamation className={styles.columnAlertIcon} title="Contains date alert" />}
                                            {sortBy === colIndex && (sortDirection === 'asc' ? <HiArrowNarrowUp className={styles.sortIcon} /> : <HiArrowNarrowDown className={styles.sortIcon} />)}
                                        </span>
                                        {!col.defaultColumn && (
                                            <button
                                                    type="button"
                                                    className={styles.editColumnBtn}
                                                    onClick={() => showEditForm(col)}
                                                    title="Edit column"
                                                    aria-label="Edit column"
                                                >
                                                    <MdEdit />
                                                </button>
                                        )}
                                    </div>
                                </th>
                            );
                        })}
                    </tr>
                </thead>
                <tbody>
                    {displayedItems.length === 0 ? (
                        <tr>
                            <td colSpan={headerCellCount} className={styles.emptyCell}>
                                No items
                            </td>
                        </tr>
                    ) : (
                    displayedItems.map((item, rowIndex) => {
                        const rowKey = item.id ?? item[identifierColumnName] ?? rowIndex;
                        const itemId = String(item[identifierColumnName] ?? item.id ?? '');
                        const isHighlighted = highlightedRowId != null && String(highlightedRowId) === itemId;
                        return (
                            <tr key={rowKey} className={isHighlighted ? styles.rowBroughtToTop : undefined}>
                                <td className={`${styles.identifierCell} ${itemCategory ? styles.identifierCellWrappable : ''}`}>
                                    {itemCategory ? (
                                        <PopupOnClick text={item[identifierColumnName] ?? "-"} itemType={itemCategory} itemId={item.id ?? item[identifierColumnName]} />
                                    ) : (
                                        item[identifierColumnName] ?? "-"
                                    )}
                                </td>
                                {columns.map((col, colIndex) => {
                                    const cellKey = `${rowKey}|${col.id ?? col.valueKey}`;
                                    const isInvalidDate = invalidDateCell === cellKey;
                                    const displayValue = getInputValue(item, col, editState, rowKey, defaultValueByColumn, false, overrides);
                                    const colType = col.type?.toLowerCase?.() ?? 'text';
                                    const colEditable = col.editable !== false;
                                    const inputTdProps = !colEditable
                                        ? { className: `${styles.inputCell} ${styles.inputCellReadOnly}`, title: READ_ONLY_CELL_HINT }
                                        : { className: styles.inputCell };
                                    const fmtCtx = { item, rowKey, overrides, column: col };
                                    const defaultConditionalStyle = conditionalFormattingStyle(getConditionalFormattingColor(col, displayValue, defaultValueByColumn, fmtCtx));

                                    if (colType === 'select') {
                                        const opts = Array.isArray(col.options) ? col.options : [];
                                        let selectValue = getInputValue(item, col, editState, rowKey, defaultValueByColumn, true, overrides);
                                        const colInterpreter = defaultValueByColumn?.[col.valueKey]?.interpreter ?? defaultValueByColumn?.[col.id]?.interpreter;
                                        selectValue = resolveSelectValueForControl(selectValue, opts, colInterpreter);
                                        const allowEmpty = col.valueKey !== 'trailerStatus';
                                        if (!allowEmpty && !selectValue && opts.length > 0) {
                                            selectValue = opts[0];
                                        }
                                        const selectConditionalStyle = conditionalFormattingStyle(getConditionalFormattingColor(col, selectValue, defaultValueByColumn, fmtCtx));
                                        return (
                                            <td key={colIndex} {...inputTdProps}>
                                                <select
                                                    className={`${styles.cellInput} ${styles.cellInputSelect} ${!colEditable ? styles.cellReadOnly : ''}`}
                                                    style={selectConditionalStyle}
                                                    disabled={!colEditable}
                                                    value={selectValue}
                                                    onChange={(e) => {
                                                        const val = e.target.value;
                                                        setEditState(prev => ({ ...prev, [cellKey]: val }));
                                                    }}
                                                    onBlur={(e) => {
                                                        if (!colEditable) return;
                                                        const val = e.target.value;
                                                        broadcastCellChange(broadCastMessage, rowKey, col, val === '' ? null : val, defaultValueByColumn, cellValuesRef);
                                                    }}
                                                >
                                                    {allowEmpty && <option value="">—</option>}
                                                    {opts.map((opt, i) => {
                                                        const label = typeof colInterpreter === 'function' ? colInterpreter(opt) : opt;
                                                        return <option key={i} value={opt}>{label}</option>;
                                                    })}
                                                </select>
                                            </td>
                                        );
                                    }

                                    if (colType === 'date') {
                                        const cache = cellAlertCacheRef.current;
                                        let isDateAlert = cache[cellKey];
                                        if (isDateAlert === undefined) {
                                            const rawVal = getRawValueForDate(item, col, rowKey, overrides, editState);
                                            isDateAlert = !!(rawVal && isDateInAlertRange(rawVal, col, currentTime));
                                            cache[cellKey] = isDateAlert;
                                        }
                                        return (
                                            <td key={colIndex} {...inputTdProps}>
                                                <TableDateCellWithCalendar
                                                    cellKey={cellKey}
                                                    rowKey={rowKey}
                                                    col={col}
                                                    displayValue={displayValue}
                                                    isInvalidDate={isInvalidDate}
                                                    isDateAlert={isDateAlert}
                                                    setInvalidDateCell={setInvalidDateCell}
                                                    setEditState={setEditState}
                                                    broadCastMessage={broadCastMessage}
                                                    defaultValueByColumn={defaultValueByColumn}
                                                    cellValuesRef={cellValuesRef}
                                                    cellAlertCacheRef={cellAlertCacheRef}
                                                    editable={colEditable}
                                                    conditionalStyle={defaultConditionalStyle}
                                                />
                                            </td>
                                        );
                                    }

                                    const isTextType = colType === 'text';
                                    const isEditingText = isTextType && colEditable && editingTextCellKey === cellKey;
                                    return (
                                        <td key={colIndex} {...inputTdProps}>
                                            {isEditingText ? (
                                                <textarea
                                                    ref={(el) => {
                                                        editingTextareaRef.current = el;
                                                        if (el) {
                                                            el.style.height = 'auto';
                                                            el.style.height = `${Math.max(el.scrollHeight, 24)}px`;
                                                            el.focus();
                                                        }
                                                    }}
                                                    className={`${styles.cellInput} ${styles.cellTextarea}`}
                                                    style={defaultConditionalStyle}
                                                    value={displayValue}
                                                    onChange={(e) => {
                                                        const val = e.target.value;
                                                        setEditState(prev => ({ ...prev, [cellKey]: val }));
                                                        e.target.style.height = 'auto';
                                                        e.target.style.height = `${Math.max(e.target.scrollHeight, 24)}px`;
                                                    }}
                                                    onBlur={(e) => {
                                                        editingTextareaRef.current = null;
                                                        const val = e.target.value?.trim?.() ?? '';
                                                        const h = e.target.scrollHeight;
                                                        textCellHeightsRef.current[cellKey] = h;
                                                        const key = col.valueKey ?? col.id;
                                                        setOverrides(prev => ({ ...prev, [rowKey]: { ...prev[rowKey], [key]: val === '' ? null : val } }));
                                                        broadcastCellChange(broadCastMessage, rowKey, col, val === '' ? null : val, defaultValueByColumn, cellValuesRef);
                                                        setEditState(prev => {
                                                            const next = { ...prev };
                                                            delete next[cellKey];
                                                            return next;
                                                        });
                                                        setEditingTextCellKey(null);
                                                    }}
                                                    onKeyDown={(e) => {
                                                        if (e.key === 'Enter' && !e.shiftKey) {
                                                            e.preventDefault();
                                                            e.target.blur();
                                                        }
                                                    }}
                                                    onClick={(e) => e.stopPropagation()}
                                                    autoComplete="off"
                                                    rows={1}
                                                />
                                            ) : isTextType ? (
                                                <div
                                                    className={`${styles.cellTextDisplay} ${styles.cellInput} ${!colEditable ? styles.cellReadOnly : ''} ${defaultConditionalStyle ? styles.cellConditionalFormatted : ''}`}
                                                    style={{
                                                        minHeight: textCellHeightsRef.current[cellKey] ? `${textCellHeightsRef.current[cellKey]}px` : undefined,
                                                        ...(defaultConditionalStyle || {}),
                                                    }}
                                                    onClick={() => {
                                                        if (editingTextCellKey && editingTextCellKey !== cellKey && editingTextareaRef.current) {
                                                            const el = editingTextareaRef.current;
                                                            const val = el.value?.trim?.() ?? '';
                                                            const [prevRowKey, prevColKey] = editingTextCellKey.split('|');
                                                            const prevCol = columns.find(c => String(c.id ?? c.valueKey) === prevColKey);
                                                            if (prevCol) {
                                                                const prevKey = prevCol.valueKey ?? prevCol.id;
                                                                setOverrides(prev => ({ ...prev, [prevRowKey]: { ...prev[prevRowKey], [prevKey]: val === '' ? null : val } }));
                                                                broadcastCellChange(broadCastMessage, prevRowKey, prevCol, val === '' ? null : val, defaultValueByColumn, cellValuesRef);
                                                                textCellHeightsRef.current[editingTextCellKey] = el.scrollHeight;
                                                            }
                                                            setEditState(prev => {
                                                                const next = { ...prev };
                                                                delete next[editingTextCellKey];
                                                                return next;
                                                            });
                                                        }
                                                        if (!colEditable) {
                                                            setEditingTextCellKey(null);
                                                            return;
                                                        }
                                                        setEditingTextCellKey(cellKey);
                                                    }}
                                                >
                                                    {displayValue ? linkifyText(displayValue) : '\u00A0'}
                                                </div>
                                            ) : (
                                                <input
                                                    type="text"
                                                    readOnly={!colEditable}
                                                    tabIndex={colEditable ? undefined : -1}
                                                    className={`${styles.cellInput} ${!colEditable ? styles.cellReadOnly : ''}`}
                                                    style={defaultConditionalStyle}
                                                    value={displayValue}
                                                    onChange={(e) => {
                                                        if (!colEditable) return;
                                                        const val = e.target.value;
                                                        setEditState(prev => ({ ...prev, [cellKey]: val }));
                                                    }}
                                                    onBlur={(e) => {
                                                        if (!colEditable) return;
                                                        const val = e.target.value?.trim?.() ?? '';
                                                        broadcastCellChange(broadCastMessage, rowKey, col, val === '' ? null : val, defaultValueByColumn, cellValuesRef);
                                                    }}
                                                    autoComplete="off"
                                                />
                                            )}
                                        </td>
                                    );
                                })}
                            </tr>
                        );
                    })
                    )}
                </tbody>
            </table>
        </div>
    );
    const buildFormPopup = (formConfig, actionConfig) => {
        const { columns: formColumns, ...rest } = formConfig;
        return {
            content: CustomAddForm,
            popupData: {
                action: (event, postResultAction) => {
                    event.preventDefault();
                    const target = event.target;
                    const body = actionConfig.buildBody(target, formColumns);
                    actionConfig.onSubmit(body, postResultAction);
                },
                ...rest,
                columns: formColumns,
            },
            EZCloseAllowed: true,
        };
    };

    const showAddForm = () => {
        addPopup(buildFormPopup({
            formTitle: "Add Column",
            submitTitle: "Add",
            name: "Add",
            method: "POST",
            columns: COLUMN_ADD_FORM.columns,
        }, {
            buildBody: (target, formColumns) => {
                const body = {};
                for (const clmn of formColumns) {
                    const inputField = target.querySelector(`input[name="${clmn.name}"]`) ?? target.querySelector(`select[name="${clmn.name}"]`);
                    if (inputField && !inputField.disabled) {
                        body[clmn.name] = clmn.processBeforeSending ? clmn.processBeforeSending(inputField.value) : inputField.value;
                    }
                }
                return body;
            },
            onSubmit: (body, postResultAction) => {
                const options = body.options ? String(body.options).split(',').map(s => s.trim()).filter(Boolean) : [];
                const newColumn = body.type === "Selectable Options" ? { id: body.id, label: body.label, type: body.type, options } : body;
                broadCastMessage?.({ structuralChange: true, action: 'add', column: newColumn });
                setColumns(prev => [...prev, newColumn]);
                postResultAction(true);
            },
        }));
    };

    const showEditForm = (column) => {
        const editFormConfig = buildColumnEditForm(column, { broadCastMessage, setColumns });
        addPopup(buildFormPopup({
            formTitle: "Edit Column",
            submitTitle: "Save",
            name: "Edit",
            method: "POST",
            columns: editFormConfig.columns,
        }, {
            buildBody: (target, formColumns) => {
                const body = { ...column };
                for (const clmn of formColumns) {
                    const inputField = target.querySelector(`input[name="${clmn.name}"]`) ?? target.querySelector(`select[name="${clmn.name}"]`);
                    if (inputField && !inputField.disabled) {
                        body[clmn.name] = clmn.processBeforeSending ? clmn.processBeforeSending(inputField.value) : inputField.value;
                    }
                }
                return body;
            },
            onSubmit: (body, postResultAction) => {
                const updatedColumn = { ...column, ...body };
                const colType = updatedColumn.type?.toLowerCase?.() ?? 'text';
                if (colType === 'date') {
                    const colId = updatedColumn.id ?? updatedColumn.valueKey;
                    const cache = cellAlertCacheRef.current;
                    for (const cacheKey of Object.keys(cache)) {
                        if (cacheKey.endsWith(`|${colId}`)) delete cache[cacheKey];
                    }
                }
                broadCastMessage?.({ structuralChange: true, action: 'edit', column: updatedColumn });
                setColumns(prev => prev.map(c => (c.id === column.id ? updatedColumn : c)));
                postResultAction(true);
            },
        }));
    };

    return (
        <>
        <div className={styles.searchContainer}>
            <input
                type="text"
                className={styles.searchInput}
                placeholder={`Filter by ${headerLabel}...`}
                value={identifierFilter}
                onChange={(e) => setIdentifierFilter(e.target.value)}
                autoComplete="off"
            />
            {identifierFilter && (
                <button
                    type="button"
                    className={styles.searchClearBtn}
                    onClick={() => setIdentifierFilter('')}
                    title="Clear filter"
                    aria-label="Clear filter"
                >
                    <MdClose />
                </button>
            )}
        </div>
        <div className={styles.tableWithHeaderAction}>
            {tableEl}
            <AddButton customSize="1.5rem" action={showAddForm} />
        </div>
        </>
        
    );
};
